import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from './auth/auth.service';
import { catchError, of, switchMap } from 'rxjs';
import { UserService } from './services/user.service';

// Priority order: calls-historic → realtime-report → contingencies-report
const ROUTE_PERMISSIONS: { route: string; permission: string }[] = [
  { route: '/dashboard/calls-historic', permission: 'custom:Download' },
  { route: '/dashboard/realtime-report', permission: 'custom:Real-time' },
  { route: '/dashboard/contingencies-report', permission: 'custom:Contingencies' },
];

function navigateToFirstAvailable(user: any, router: Router): boolean {
  const firstAvailable = ROUTE_PERMISSIONS.find(
    (r) => user.userData[r.permission] === '1'
  );
  if (firstAvailable) {
    router.navigate([firstAvailable.route]);
  } else {
    router.navigate(['/403']);
  }
  return false;
}

export const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);
  
  return authService.checkAuth().pipe(
    switchMap(async (res) => {
      const user = userService.getUser();
      if (user) {
        return true;
      }
      if (!res.isAuthenticated) {
        userService.removeUser();
        authService.login();
        return false;
      }
      
      const result = await userService.setUser(res);
      
      if (!result.status) {
        console.error('Error setting user:', result.message);
        router.navigate(['/403']);
        return false;
      }
      
      return true;
    }),
    catchError((error) => {
      console.error('Auth guard error:', error);
      router.navigate(['/403']);
      return of(false);
    }),
  );
};


export const canActivateRealtime: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.checkAuth().pipe(
    switchMap(async (res) => {
      const user = userService.getUser();
      if (user) {
        if (user.userData["custom:Real-time"] === "1") return true;
        return navigateToFirstAvailable(user, router);
      }
      if (!res.isAuthenticated) {
        userService.removeUser();
        authService.login();
        return false;
      }
      const result = await userService.setUser(res);
      if (!result.status) {
        router.navigate(['/403']);
        return false;
      }
      const updatedUser = userService.getUser();
      if (updatedUser?.userData["custom:Real-time"] !== "1") {
        return navigateToFirstAvailable(updatedUser, router);
      }
      return true;
    }),
    catchError((error) => {
      console.error('Realtime guard error:', error);
      router.navigate(['/403']);
      return of(false);
    }),
  );
};


export const canActivateDownload: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.checkAuth().pipe(
    switchMap(async (res) => {
      const user = userService.getUser();
      if (user) {
        if (user.userData["custom:Download"] === "1") return true;
        return navigateToFirstAvailable(user, router);
      }
      if (!res.isAuthenticated) {
        userService.removeUser();
        authService.login();
        return false;
      }
      const result = await userService.setUser(res);
      if (!result.status) {
        router.navigate(['/403']);
        return false;
      }
      const updatedUser = userService.getUser();
      if (updatedUser?.userData["custom:Download"] !== "1") {
        return navigateToFirstAvailable(updatedUser, router);
      }
      return true;
    }),
    catchError((error) => {
      console.error('Download guard error:', error);
      router.navigate(['/403']);
      return of(false);
    }),
  );
};
export const canActivateContingencies: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.checkAuth().pipe(
    switchMap(async (res) => {
      const user = userService.getUser();
      if (user) {
        if (user.userData["custom:Contingencies"] === "1") return true;
        return navigateToFirstAvailable(user, router);
      }
      if (!res.isAuthenticated) {
        userService.removeUser();
        authService.login();
        return false;
      }
      const result = await userService.setUser(res);
      if (!result.status) {
        router.navigate(['/403']);
        return false;
      }
      const updatedUser = userService.getUser();
      if (updatedUser?.userData["custom:Contingencies"] !== "1") {
        return navigateToFirstAvailable(updatedUser, router);
      }
      return true;
    }),
    catchError((error) => {
      console.error('Contingencies guard error:', error);
      router.navigate(['/403']);
      return of(false);
    }),
  );
};
